import * as React from 'react';
import { useParams } from 'react-router-dom';
import { TabPanel, TabView } from 'primereact/tabview';
import GroupOverview from '../../components/groups/GroupOverview';
import GroupHistory from '../../components/groups/GroupHistory';
import PlanDetailsModal from '../../components/groups/PlanDetailsModal';
import ReliefCalculator from '../../components/groups/ReliefCalculator';
import { fetchGroup, fetchGroupRates, resetGroupRates, setRerateInputs, toggleRateReliefAvailability } from '../../store/group';
import { useAppSelector, useAppDispatch } from '../../store';
import { APIStatus, GroupStatus } from '../../utils/types';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { format, toDate } from 'date-fns';
import { dateFormat } from '../../utils/date';
import GroupBadges from '../../components/groups/GroupBadges';
import ReliefSummary from '../../components/groups/ReliefSummary';
import { Button } from 'primereact/button';
import { InputNumber, InputNumberChangeEvent } from 'primereact/inputnumber';
import RoleGuard from '../../components/auth/RoleGuard';
import { UserRole } from '../../store/auth';
import RerateResponseForm from '../../components/groups/actuary/RerateResponseForm';
import Tier2ResponseForm from '../../components/groups/actuary/Tier2ResponseForm';
import GroupBanner from '../../components/groups/GroupBanner';
import HighRiskRequestForm from '../../components/groups/modals/HighRiskRequestForm';
import HighRiskResponseForm from '../../components/groups/actuary/HighRiskResponseForm';
import HighRiskRemovalRequestForm from '../../components/groups/modals/HishRiskRemovalRequestForm';
import { canGroupBeLocked, hasOpenRequest, isGroupHighRisk } from '../../utils/groupList';
import GroupActionsMenu from '../../components/groups/GroupActionsMenu';
import GroupAssignedTo from '../../components/groups/GroupAssignedTo';
import { DEFAULT_RR } from '../../utils/rerate';

export default function GroupDetail() {
  const { policyNumber, timePeriodDate } = useParams();
  const dispatch = useAppDispatch();
  
  const [inputRR, setInputRR] = React.useState<number | null>(DEFAULT_RR);
  const [showPlanDetailsModal, setShowPlanDetailsModal] = React.useState<boolean>(false);
  const [isChangingRRStatus, setIsChangingRRStatus] = React.useState<boolean>(false);
  const { group, groupLoaded, apiState, rates, originalRates, ratesApiState, reRateInputs } = useAppSelector((state) => state.group);
  const [activeIndex, setActiveIndex] = React.useState<number>(0);

  const [showRerateForm, setShowRerateForm] = React.useState<boolean>(false);
  const [showTier2Form, setShowTier2Form] = React.useState<boolean>(false);

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  React.useEffect(() => {
    if (policyNumber && timePeriodDate) {
      dispatch(fetchGroup({ policyNumber, timePeriodDate: toDate(timePeriodDate) }));
      dispatch(resetGroupRates({ policyNumber, timePeriodDate: toDate(timePeriodDate) }));
      dispatch(setRerateInputs({}));
      setInputRR(DEFAULT_RR);
    }
  }, [policyNumber, timePeriodDate]);

  function updateRates(e: InputNumberChangeEvent) {
    const value = e.value;
    setInputRR(e.value);

    if (!showRerateForm) {    
      dispatch(fetchGroupRates({
        policyNumber,
        timePeriodDate: toDate(timePeriodDate!)!,
        ...reRateInputs,
        rateReliefAmount: value || value === 0 ? (value / 100) : undefined,
      }));
    }
  }

  function handleResponseCompletion() {
    setShowTier2Form(false);
    setShowRerateForm(false);

    if (policyNumber && timePeriodDate) {
      dispatch(fetchGroup({ policyNumber, timePeriodDate: toDate(timePeriodDate!) }));
      dispatch(resetGroupRates({
        policyNumber,
        timePeriodDate: toDate(timePeriodDate!)
      }));
    }
  }

  function showAndScrollToRerateForm() {
    setShowRerateForm(true);
    setShowTier2Form(false);

    setTimeout(() => {
      const formElement = document.querySelector('[data-rerate-form]');
      formElement?.scrollIntoView()
    }, 0);
  }

  function showAndScrollToTier2Form() {
    setShowTier2Form(true);
    setShowRerateForm(false);

    setTimeout(() => {
      const formElement = document.querySelector('[data-tier2-form]');
      formElement?.scrollIntoView()
    }, 0);
  }

  if (apiState === APIStatus.ERROR || ratesApiState === APIStatus.ERROR) {
    return (
      <div className="flex flex-col py-10">
        <Message
          icon="pi pi-exclamation-circle"
          severity="error"
          text={(
            <>
              There was an error loading the group, <span className="underline cursor-pointer" onClick={() => dispatch(fetchGroup({ policyNumber, timePeriodDate }))}>click here</span> to try again
            </>
          )}
        />
      </div>
    )
  }

  if (apiState === APIStatus.LOADING || !groupLoaded) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 p-10">
        <ProgressSpinner className="w-[40px] h-[40px]" />
        <div className="text-xs">Loading group with policy number <strong>{policyNumber}</strong></div>
      </div>
    );
  }

  function viewNotesTab(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    setActiveIndex(1);
  }

  async function handleReliefStatusChange(v: boolean) {
    setIsChangingRRStatus(true);
    await toggleRateReliefAvailability(group?.policyDetailModel?.policyNumber!, v, group?.policyDetailModel?.timePeriodDate!);

    if (policyNumber && timePeriodDate) {
      dispatch(fetchGroup({
        policyNumber,
        timePeriodDate: group?.policyDetailModel?.timePeriodDate!
      }));
      dispatch(resetGroupRates({ policyNumber, timePeriodDate: group?.policyDetailModel?.timePeriodDate! }));
      setIsChangingRRStatus(false);
    }
  }

  return (
    <>
      <GroupBanner group={group!} rates={rates} onViewNotes={viewNotesTab} />

      <section className="grid gap-4 grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-4">
          <div className="grid gap-5 p-6 bg-white rounded">
            <div className="grid gap-1">
              <div className="flex gap-4">
                <div className="flex-1">
                  <h1 className="text-lg font-bold text-allstate-blue">
                    {group?.policyDetailModel?.policyNumber}: {group?.policyDetailModel?.groupName}
                  </h1>
                  <p className="text-xs text-grey-8">
                    <span className="pi pi-calendar"></span> Last updated: {format((hasOpenRequest(group) ? group?.requestedDate! : group?.policyDetailModel?.createdDate!), 'MM/dd/yyyy') } &bull; {format((hasOpenRequest(group) ? group?.requestedDate! : group?.policyDetailModel?.createdDate!), 'H:mm') } by {hasOpenRequest(group) ? group?.requestedBy : group?.policyDetailModel?.createdBy}
                  </p>
                  <GroupAssignedTo group={group!} />
                </div>
                <GroupActionsMenu
                  assignedTo={group?.assignedTo}
                  groupId={group?.policyDetailModel?.policyNumber!}
                  timePeriodDate={group?.policyDetailModel?.timePeriodDate!}
                  groupName={group?.policyDetailModel?.groupName!}
                  onComplete={() => dispatch(fetchGroup(policyNumber as string)) }
                  requestStatus={group?.requestStatus as GroupStatus}
                />
              </div>
              
              <div className="flex flex-wrap gap-2 text-sm">
                {group?.policyDetailModel?.originalEffectiveDate && (
                  <p><strong>Effective date:</strong> {format(group?.policyDetailModel.originalEffectiveDate, dateFormat)}</p>
                )}
                {group?.policyDetailModel?.reissueDate && (
                  <p><strong>Reissue:</strong> {format(group.policyDetailModel?.reissueDate, 'MMMM')}</p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <GroupBadges group={group} />
              <div onClick={() => setShowPlanDetailsModal(true)} className="text-sm underline cursor-pointer text-allstate-blue">view plan details</div>
            </div>

            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
              <TabPanel header="Overview">
                <div className="flex flex-col gap-4">
                  <GroupOverview group={group} rates={originalRates} onReliefStatusChange={handleReliefStatusChange} isLoading={isChangingRRStatus} />
                  <ReliefSummary
                    group={group}
                    rates={rates}
                    appliedRR={inputRR}
                    onActionRerate={() => showAndScrollToRerateForm()}
                    onActionTier2={() => showAndScrollToTier2Form()}
                  />

                  {group?.policyDetailModel?.dentalVisionGroup && (
                    <Message
                      severity="error"
                      text={
                        <div className="flex flex-col gap-1">
                          <div className="font-bold">This group has a dental and vision plan</div>
                          <p>Remove dental/vision plan within web quoting plan tab, then apply rate relief, then re-apply dental/vision plan in Web Quoting.</p>
                        </div>
                      }
                    />
                  )}

                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-2">
                      <label htmlFor="rr_amount" className="font-bold">Rate relief amount (%)</label>
                      <div>
                        <InputNumber
                          id="rr_amount"
                          value={inputRR}
                          onChange={updateRates}
                          min={0}
                          maxFractionDigits={2}
                        />
                      </div>
                    </div>
                    <Button severity="secondary" label="reset" disabled={!inputRR} rounded onClick={() => setInputRR(DEFAULT_RR)}/>
                  </div>
                </div>
              </TabPanel>
              <TabPanel header="History">
                <GroupHistory group={group} />
              </TabPanel>
            </TabView>
          </div>

          <RoleGuard role={UserRole.ACTUARY}>

            {/* TODO: check if we have a rerate / tier 2 request */}
            <div className="p-6 bg-white rounded empty:hidden">
                {(!showTier2Form && (showRerateForm || group?.requestStatus === GroupStatus.RERATE_REQUESTED)) && 
                  <div data-rerate-form>
                    <RerateResponseForm group={group} onComplete={handleResponseCompletion} rates={rates} inputRR={inputRR} />
                  </div>
                }
                {(!showRerateForm && (showTier2Form || group?.requestStatus === GroupStatus.TIER2_REQUESTED)) && 
                  <div data-tier2-form>
                    <Tier2ResponseForm group={group} onComplete={handleResponseCompletion} rates={rates} />
                  </div>
                }
            </div>
          </RoleGuard>

          <div className="p-6 bg-white empty:hidden">
            {isGroupHighRisk(group) && (
              <>
                <RoleGuard role={UserRole.ACCOUNT_MANAGER}>
                  <HighRiskRemovalRequestForm group={group} onComplete={handleResponseCompletion} />
                </RoleGuard>
                <RoleGuard role={UserRole.ACTUARY}>
                  <HighRiskResponseForm group={group} onComplete={handleResponseCompletion} />
                </RoleGuard>
              </>
            )}

            {!isGroupHighRisk(group) && canGroupBeLocked(group) && (
              <RoleGuard role={UserRole.ACTUARY}>
                <HighRiskRequestForm group={group} onComplete={handleResponseCompletion} />
              </RoleGuard>
            )}
          </div>

        </div>

        <div>
          <div className="sticky top-4">
            <ReliefCalculator group={group} rates={rates} status={ratesApiState} />
          </div>
        </div>
      </section>

      <PlanDetailsModal group={group} rates={originalRates} onHide={() => setShowPlanDetailsModal(false)} visible={showPlanDetailsModal} />
    </>
  )
}